import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/theme.context';
import { LuAreaChart, LuBarChart2, LuLineChart, LuArrowRight, LuMaximize2, LuX, LuLayers } from 'react-icons/lu';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { IconBadge } from '@/components/atoms/IconBadge';
import { Text } from '@/components/atoms/AppText';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { GtChart } from './GtChart';
import type { GtChartType, GtReferenceLine } from './GtChart';
import { buildVdiPalette, buildAlertPalette } from '@/constant/chart/chartPalette.constant';
import { CHART_PALETTE } from './GtChart';

// ── Chart variant ──────────────────────────────────────────────────────────────────────
export type ChartVariant = 'area' | 'bar' | 'line' | 'stacked';

// Default all charts to stacked — shows both composition and total in one view;
// users can switch to bar / line / area via the segmented control.
const CHART_VARIANTS: Record<string, ChartVariant> = {
  mmsDevices: 'stacked',
  mmsMarkets: 'stacked',
  mmsProducts: 'stacked',
  mmsSales: 'stacked',
  alerts: 'stacked',
};


export interface DashboardChartData {
  series: { name: string; data: (number | string | null | undefined)[] }[];
  options: {
    xaxis?: { categories?: (string | number)[] };
    labels?: (string | number)[];
    [key: string]: unknown;
  };
}

interface DashboardChartProps {
  chartKey: string;
  title: string;
  subtitle?: string;
  /** Custom icon rendered in the header badge. Defaults to the active chart-type icon. */
  icon?: React.ReactNode;
  data: DashboardChartData;
  height?: number;
  forceVariant?: ChartVariant;
  className?: string;
  /** data-testid for automation */
  testId?: string;
  /** Optional "View all" navigation link shown in chart header */
  href?: string;

  // ── GtChart pass-through props ──────────────────────────────────────────────
  /** Show legend. Default: auto (shown when series > 1). */
  showLegend?: boolean;
  /** Legend placement. Default: 'bottom' */
  legendPosition?: 'bottom' | 'top' | 'right';
  /** Show horizontal grid lines. Default: true */
  showGrid?: boolean;
  /** Play entry animation. Default: true */
  animate?: boolean;
  /** Enable zoom/pan on the chart. Default: true */
  enableZoom?: boolean;
  /** Stack bar series. Default: false */
  stacked?: boolean;
  /** Line/area stroke width. Default: 2 */
  strokeWidth?: number;
  /** Area fill opacity at the top. Default: 0.10 */
  fillOpacity?: number;
  /** Bar top corner radius. Default: 4 */
  barRadius?: number;
  /** Fraction of band width used as gap between bar groups (0–1). Default: 0.28 */
  barGroupPadding?: number;
  /** Label shown when there is no data. Default: 'No data available' */
  noDataLabel?: string;
  /** Custom tooltip value formatter */
  formatTooltipValue?: (value: number, seriesName: string, catLabel: string) => string;
  /** Show stacked column totals label above each bar column (stacked mode only). Default: false */
  showTotalsLabel?: boolean;
  /** Custom color for the total-sum labels above stacked bars. Defaults to theme-aware slate. */
  totalsLabelColor?: string;
  /** Dim non-hovered series when cursor is over a column. Default: false */
  highlightOnHover?: boolean;
  /**
   * Group series into independent stacked sub-bars per band.
   * [[0,1],[2,3,4]] → left bar = series 0+1 stacked, right bar = series 2+3+4 stacked.
   * When set, overrides the plain stacked/grouped bar logic.
   */
  stackGroups?: number[][];

  // ── Advanced GtChart pass-through props ────────────────────────────────────
  /** Per-series color overrides. Falls back to the chart's built-in palette. */
  colors?: string[];
  /** Y-axis tick label formatter */
  formatY?: (value: number) => string;
  /** X-axis tick label formatter */
  formatX?: (value: string | number, index: number) => string;
  /** Tooltip title formatter — overrides the category header in the tooltip */
  formatTooltipTitle?: (rawCat: string | number, index: number) => string;
  /** Reference lines drawn at specific Y values */
  referenceLines?: GtReferenceLine[];
  /** Fixed minimum Y value. Default: 0 */
  minY?: number;
  /** Number of Y-axis ticks. Default: 5 */
  tickCount?: number;
  /** Radius of data-point dots on line/area charts. 0 = no dots. */
  dotSize?: number;
  /** Bezier curve tension for line/area charts (0–1). Default: 0.3 */
  smooth?: number;
  /** Show value labels above each bar / on each data point. Default: false */
  showDataLabels?: boolean;
  /** Apply vertical gradient fill to bars. Default: true */
  barGradient?: boolean;
  /** Donut inner-radius ratio (0–1). Default: 0.55 */
  donutInnerRatio?: number;
  /** Label shown in the donut hole. Defaults to formatted total. */
  donutLabel?: string;
  /** Callback when the user clicks a data point or bar */
  onDataPointClick?: (seriesIndex: number, dataIndex: number, value: number) => void;
}

const VARIANT_ICONS: Record<ChartVariant, React.ElementType> = {
  area: LuAreaChart,
  bar: LuBarChart2,
  line: LuLineChart,
  stacked: LuLayers,
};

const VARIANT_LABELS: Record<ChartVariant, string> = {
  area: ORGANISM_PROPS.DASHBOARD_CHART.VARIANT_AREA,
  bar: ORGANISM_PROPS.DASHBOARD_CHART.VARIANT_BAR,
  line: ORGANISM_PROPS.DASHBOARD_CHART.VARIANT_LINE,
  stacked: 'Stacked',
};

const VARIANTS: ChartVariant[] = ['stacked', 'bar', 'line', 'area'];

// Map chart variant to GtChart type
const toGtType = (v: ChartVariant): GtChartType => (v === 'stacked' ? 'stacked' : v as GtChartType);

const fmtTooltipVal = (v: number): string =>
  Number.isFinite(v) ? new Intl.NumberFormat().format(v) : '—';

const DashboardChart: React.FC<DashboardChartProps> = ({
  chartKey,
  title,
  subtitle,
  icon,
  data,
  height = 240,
  forceVariant,
  className = '',
  testId,
  href,
  // GtChart pass-through props with sensible defaults
  showLegend,
  legendPosition = 'bottom',
  showGrid = true,
  animate = true,
  enableZoom = true,
  stacked = false,
  strokeWidth = 2,
  fillOpacity = 0.10,
  barRadius = 5,
  barGroupPadding = 0.25,
  noDataLabel = 'No data available',
  formatTooltipValue,
  showTotalsLabel = false,
  totalsLabelColor,
  highlightOnHover = true,
  stackGroups,
  colors: colorsOverride,
  formatY,
  formatX,
  formatTooltipTitle,
  referenceLines,
  minY,
  tickCount,
  dotSize,
  smooth,
  showDataLabels,
  barGradient,
  donutInnerRatio,
  donutLabel,
  onDataPointClick,
}) => {
  const { isDark, colors } = useTheme();

  const defaultVariant = forceVariant ?? CHART_VARIANTS[chartKey] ?? 'bar';
  const [activeVariant, setActiveVariant] = useState<ChartVariant>(defaultVariant);
  const [isExpanded, setIsExpanded] = useState(false);

  // Lock body scroll when expanded + Escape to close
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded]);

  // ── Series colors ────────────────────────────────────────────────────────────
  // VDI charts (grouped-stacked): [[0,1],[2,3]]     left=Success stack, right=Failure stack
  // Alert chart (grouped-stacked): [[0,1],[2,3]]    left=Activity stack, right=Failure stack
  // Received / Failed are stat-card totals only — NOT chart series for either chart type
  const isVdiChart = ['mmsMarkets', 'mmsSales', 'mmsProducts', 'mmsDevices'].includes(chartKey);
  const isAlertChart = chartKey === 'alerts';
  const resolvedStackGroups = stackGroups ??
    (isVdiChart ? [[0, 1], [2, 3]] : isAlertChart ? [[0, 1], [2, 3]] : undefined);
  const resolvedStackGroupLabels = isVdiChart
    ? ['Inbound', 'Failures']
    : isAlertChart
      ? ['Activity', 'Failures']
      : undefined;

  // ── Theme-driven palette ───────────────────────────────────────────────────
  // 3 color families: theme (primary + lighter), amber (open/active), red (all failures)
  // colorsOverride lets callers bypass this entirely for custom charts.
  const resolvedColors = useMemo(() => {
    if (colorsOverride) return colorsOverride;
    if (isAlertChart) return buildAlertPalette(colors.primary);
    if (isVdiChart) return buildVdiPalette(colors.primary);
    return CHART_PALETTE;
  }, [colorsOverride, colors.primary, isVdiChart, isAlertChart]);

  // ── Category extraction ────────────────────────────────────────────────────
  const categories = useMemo(
    () => data?.options?.xaxis?.categories ?? data?.options?.labels ?? [],
    [data?.options],
  );

  // ── Series normalization ───────────────────────────────────────────────────
  const series = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        name: s.name ?? '',
        data: (s.data ?? []).map((v: any) =>
          typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) || 0 : 0,
        ),
      })),
    [data?.series],
  );

  // Per-category tooltip total override — read from options (e.g. receivedAlerts for alerts chart)
  const tooltipTotals = useMemo(
    () => (data?.options as any)?.tooltipTotals as number[] | undefined,
    [data?.options],
  );

  const hasNoData =
    series.length === 0 || series.every((s) => s.data.every((v) => v === 0));

  const ActiveIcon = VARIANT_ICONS[activeVariant];
  const headerIcon = icon ?? <ActiveIcon size={14} />;

  // Chart-type switcher + optional View All link — passed as headerRight to SectionCard
  const chartSwitcher = (
    <div className="flex items-center gap-2">
      {href && !isExpanded && (
        <Link
          to={href}
          className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold transition-opacity hover:opacity-60"
          style={{ color: colors.primary }}
        >
          View all <LuArrowRight size={9} />
        </Link>
      )}
      <SegmentedControl
        size="md"
        shape="square"
        variant="gradient"
        value={activeVariant}
        onChange={(v) => setActiveVariant(v as ChartVariant)}
        className="[&_button]:!py-1 [&_button]:!px-2"
        options={VARIANTS.map((v) => ({
          value: v,
          label: VARIANT_LABELS[v],
          icon: VARIANT_ICONS[v],
          iconOnly: true,
        }))}
      />
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md transition-opacity hover:opacity-60"
        style={{ color: colors.primary }}
        title="Expand fullscreen"
      >
        <LuMaximize2 size={13} />
      </button>
    </div>
  );

  const chartBody = (expandedHeight?: number) => (
    <div className="pt-2 pb-1">
      {hasNoData ? (
        <div
          className="flex flex-col items-center justify-center gap-2"
          style={{ height: expandedHeight ?? height }}
        >
          <IconBadge
            icon={<LuBarChart2 size={18} style={{ opacity: 0.5 }} />}
            size="md"
          />
          <Text as="span" size="md" weight="medium" color="muted">
            {noDataLabel}
          </Text>
        </div>
      ) : (
        <GtChart
          key={`${chartKey}-${activeVariant}-${expandedHeight ?? height}`}
          type={toGtType(activeVariant)}
          series={series}
          categories={categories}
          height={expandedHeight ?? height}
          colors={resolvedColors}
          isDark={isDark}
          animate={animate}
          showGrid={showGrid}
          showLegend={showLegend}
          legendPosition={legendPosition}
          enableZoom={enableZoom}
          stacked={stacked || activeVariant === 'stacked'}
          strokeWidth={strokeWidth}
          fillOpacity={fillOpacity}
          barRadius={barRadius}
          barGroupPadding={barGroupPadding}
          noDataLabel={noDataLabel}
          showTotalsLabel={showTotalsLabel}
          totalsLabelColor={totalsLabelColor}
          highlightOnHover={highlightOnHover}
          stackGroups={activeVariant === 'stacked' ? resolvedStackGroups : undefined}
          stackGroupLabels={activeVariant === 'stacked' ? resolvedStackGroupLabels : undefined}
          formatTooltipValue={formatTooltipValue ?? ((v) => fmtTooltipVal(v))}
          tooltipTotals={tooltipTotals}
          formatY={formatY}
          formatX={formatX}
          formatTooltipTitle={formatTooltipTitle}
          referenceLines={referenceLines}
          minY={minY}
          tickCount={tickCount}
          dotSize={dotSize}
          smooth={smooth}
          showDataLabels={showDataLabels}
          barGradient={barGradient ?? true}
          donutInnerRatio={donutInnerRatio}
          donutLabel={donutLabel}
          onDataPointClick={onDataPointClick}
        />
      )}
    </div>
  );

  const fullscreenOverlay = isExpanded
    ? createPortal(
      <div className={`fixed inset-0 z-[9999] flex flex-col ${APP_BG.base}`}>
        {/* ── Title bar ── */}
        <div
          className={`flex-shrink-0 flex items-center justify-between gap-3 px-4 h-12 border-b border-gray-200 dark:border-white/[0.06] ${APP_BG.canvas}`}
        >
          {/* Left: icon + title */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex-shrink-0 text-gray-500 dark:text-gray-400">{headerIcon}</span>
              <Text as="span" size="sm" weight="bold" color="default" className="truncate">
                {title}
              </Text>
              {subtitle && (
                <Text as="span" size="xs" color="muted" className="truncate hidden sm:block">
                  {subtitle}
                </Text>
              )}
            </div>
          </div>
          {/* Right: variant switcher + X close */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <SegmentedControl
              size="md"
              shape="square"
              variant="gradient"
              value={activeVariant}
              onChange={(v) => setActiveVariant(v as ChartVariant)}
              className="[&_button]:!py-1 [&_button]:!px-2"
              options={VARIANTS.map((v) => ({
                value: v,
                label: VARIANT_LABELS[v],
                icon: VARIANT_ICONS[v],
                iconOnly: true,
              }))}
            />
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className={`flex items-center justify-center w-7 h-7 rounded-md ${APP_TEXT.subtle} hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors`}
              title="Close (Esc)"
            >
              <LuX size={14} />
            </button>
          </div>
        </div>
        {/* ── Chart body ── */}
        <div className="flex-1 overflow-hidden flex flex-col px-5 pt-4 pb-10">
          {hasNoData ? (
            <div className="flex flex-col items-center justify-center gap-2 h-full">
              <IconBadge icon={<LuBarChart2 size={22} style={{ opacity: 0.5 }} />} size="md" />
              <Text as="span" size="md" weight="medium" color="muted">
                {noDataLabel}
              </Text>
            </div>
          ) : (
            <GtChart
              key={`${chartKey}-${activeVariant}-fs`}
              type={toGtType(activeVariant)}
              series={series}
              categories={categories}
              height={window.innerHeight - 48 - 56}
              colors={resolvedColors}
              isDark={isDark}
              animate={animate}
              showGrid={showGrid}
              showLegend={showLegend}
              legendPosition={legendPosition}
              enableZoom={enableZoom}
              stacked={stacked || activeVariant === 'stacked'}
              strokeWidth={strokeWidth}
              fillOpacity={fillOpacity}
              barRadius={barRadius}
              barGroupPadding={barGroupPadding}
              noDataLabel={noDataLabel}
              showTotalsLabel={showTotalsLabel}
              totalsLabelColor={totalsLabelColor}
              highlightOnHover={highlightOnHover}
              stackGroups={activeVariant === 'stacked' ? resolvedStackGroups : undefined}
              stackGroupLabels={activeVariant === 'stacked' ? resolvedStackGroupLabels : undefined}
              formatTooltipValue={formatTooltipValue ?? ((v) => fmtTooltipVal(v))}
              tooltipTotals={tooltipTotals}
              formatY={formatY}
              formatX={formatX}
              formatTooltipTitle={formatTooltipTitle}
              referenceLines={referenceLines}
              minY={minY}
              tickCount={tickCount}
              dotSize={dotSize}
              smooth={smooth}
              showDataLabels={showDataLabels}
              barGradient={barGradient ?? true}
              donutInnerRatio={donutInnerRatio}
              donutLabel={donutLabel}
              legendScale={1.3}
              onDataPointClick={onDataPointClick}
            />
          )}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <SectionCard
        testId={testId}
        icon={headerIcon}
        title={title}
        subtitle={subtitle}
        headerRight={chartSwitcher}
        headerClassName={APP_BG.sectionHeader}
        bodyBg={APP_BG.base}
        bodyClassName="pb-2"
        className={className}
      >
        {/* ── Chart area ── */}
        {chartBody()}
      </SectionCard>
      {fullscreenOverlay}
    </>
  );
};

export default React.memo(DashboardChart);
